/**
 * The ten maps, as ASCII.
 *
 * A level is a rectangle of characters; one character is one two-metre tile.
 * Every map puts the player at the bottom, facing up, with the way on above,
 * so up on the keys is up on the screen from the first second.
 * Keeping them as text means a level can be read and edited in place, and the
 * only thing that turns text into bodies is `parseLevel` below; the game
 * never knows about characters again.
 *
 * The legend, in the order it matters:
 *
 *   W  wall, indestructible; rows of it are merged into long fixtures
 *   #  concrete, three hits
 *   =  brick, one hit
 *   -  hedge, one hit, and a tank drives straight through it
 *   B  barrel, explodes and sets off its neighbours
 *   c  crate, pushable, breaks
 *   v  car, pushable, heavy, breaks, and a tank rolls straight over it
 *   V  a car that drives: it follows the road it starts on
 *   ~  road. Two tiles wide is a street with a lane each way; cars keep to
 *      the right of it and tanks drive over the lot
 *   P  the player, exactly one per map
 *   T  turret, static, turns and fires down a clear line
 *   E  enemy tank
 *   H  hunter, a fast light tank that comes straight at you
 *   G  gunner, a slow heavy tank with a blast shell
 *   M  mortar, static, lobs rounds over walls at where you were
 *   D  drone, tiny, quick, circles and rams
 *   Z  the boss
 *   S  a spawner, which sends reinforcements once the heat is up
 *   F  a factory: it builds tanks until it is knocked down, and the way out
 *      stays shut while one still stands
 *   X  the exit, which opens when the last enemy is gone
 *   1  Triple pickup    2  Bouncer pickup    3  Heavy pickup
 *   +  Repair, three armour back      @  Shield, the next two hits are free
 */

/** Metres per tile. Everything else in the game is metres. */
export const TILE = 2;

export type Cell = { x: number; y: number; ch: string };

export type ParsedLevel = {
  cols: number;
  rows: number;
  /** Merged runs of indestructible wall: half-extents and a centre, in metres. */
  walls: Array<{ x: number; y: number; hw: number; hh: number }>;
  /** Destructible tiles: concrete, brick, hedge. */
  tiles: Cell[];
  /** Loose bodies: barrels, crates, cars. */
  props: Cell[];
  enemies: Cell[];
  spawners: Cell[];
  /** Enemy works, which must come down before the exit opens. */
  factories: Cell[];
  pickups: Cell[];
  exits: Cell[];
  /** Road tiles, for the cars to drive and the paper to show. */
  roads: Cell[];
  player: { x: number; y: number };
};

export type Level = {
  name: string;
  objective: string;
  /** Par time in seconds; beat it for a bonus. */
  par: number;
  map: string[];
};

const yard = [
  "WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW",
  "W......................................W",
  "W..................................X...W",
  "W......................................W",
  "W.......###.......................T....W",
  "W.......###............................W",
  "W.......##1............................W",
  "W......................................W",
  "W.............................vv.......W",
  "W......................................W",
  "W......................................W",
  "W==================--==================W",
  "W......................................W",
  "W......................................W",
  "W......................................W",
  "W................B.B.B.................W",
  "W.......c..............................W",
  "W.......cc..............###............W",
  "W............---........###............W",
  "W.......................###............W",
  "W......................................W",
  "W...................P..................W",
  "W......................................W",
  "WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW",
];

const alley = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W......................W",
  "WX.....................W",
  "W...........vv.........W",
  "W.....-................W",
  "W.....--...............W",
  "W......................W",
  "W####=####....####=####W",
  "W...........=..........W",
  "W...........=..........W",
  "W...........=...c......W",
  "W..T..B.....=..........W",
  "W......................W",
  "W......................W",
  "W......................W",
  "W####=####....####=####W",
  "W..........=...........W",
  "W..........=...........W",
  "W..........=...........W",
  "W..........=2.....B.T..W",
  "W......................W",
  "W......................W",
  "W......................W",
  "W####=####....####=####W",
  "W......................W",
  "W......................W",
  "W...............c......W",
  "W..T.B..........c......W",
  "W....B.................W",
  "W......................W",
  "W......................W",
  "W####=####....####=####W",
  "W..........=...........W",
  "W..........=...........W",
  "W......c...=.....B.....W",
  "W..1...c...=.......T...W",
  "W......................W",
  "W..........P...........W",
  "W......................W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const depot = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W......................W",
  "W......................W",
  "W..........T.........X.W",
  "W......................W",
  "W......................W",
  "W...E..............E...W",
  "W......................W",
  "W....B...B...B...B.....W",
  "W..v...................W",
  "W......................W",
  "W....Bc..B...Bc..B.....W",
  "W...................##.W",
  "W...................##.W",
  "W....B...B...B...B.....W",
  "W.....c.......c........W",
  "W..........=...........W",
  "W....B...B.=.B...B...E.W",
  "W..........=...........W",
  "W.....c-...=..c.-......W",
  "W....B.-.B.=.B..-B.....W",
  "W..........3...........W",
  "W..........=...........W",
  "W....Bc..B.=.Bc..B.....W",
  "W......................W",
  "W......................W",
  "W.##.B...B...B...B.....W",
  "W.##..c.......c........W",
  "W...................v..W",
  "W....B...B...B...B..v..W",
  "W......................W",
  "W.....c.......c........W",
  "W....B...B...B...B.....W",
  "W......................W",
  "W....................1.W",
  "W......................W",
  "W......................W",
  "W..........P...........W",
  "W......................W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const fort = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W......................W",
  "W.....###............S.W",
  "W.....###..............W",
  "W.....###..1...........W",
  "W..............c.......W",
  "W...E.....+............W",
  "W.................B....W",
  "W.................B....W",
  "W......................W",
  "W......................W",
  "W......................W",
  "W.....#####==#####.....W",
  "W.....############.....W",
  "W.....##........##.....W",
  "W.....##.M....T.##.....W",
  "W.....##........##.....W",
  "W.....##........##.....W",
  "W.....##........##.....W",
  "W.2...=#...X..B.#=...3.W",
  "W.....=#.B......#=.....W",
  "W.....##........##.....W",
  "W.....##........##.....W",
  "W.....##........##.....W",
  "W.....##.T....T.##.....W",
  "W.....##........##.....W",
  "W.....############.....W",
  "W.....#####==#####.....W",
  "W......................W",
  "W......................W",
  "W......................W",
  "W....B.................W",
  "W....B.................W",
  "W.......c.........E....W",
  "W.......c..............W",
  "W..........1..###......W",
  "W.............###......W",
  "W.S........P..###......W",
  "W......................W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const hq = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W......................W",
  "W.S..................S.W",
  "W.....1..@.......1.....W",
  "W...........c..........W",
  "W......................W",
  "W...######====######...W",
  "W...################...W",
  "W...##............##...W",
  "W...##.B........B.##...W",
  "W...##............##.c.W",
  "W...##............##...W",
  "W...##............##...W",
  "W...##............##.2.W",
  "W...##..H.........##...W",
  "W...##............##...W",
  "W...##............##...W",
  "W...##............##...W",
  "W...=#............#=...W",
  "W.X.=#.B...Z....B.#=...W",
  "W...=#............#=...W",
  "W...=#............#=...W",
  "W...##............##...W",
  "W...##............##...W",
  "W...##............##...W",
  "W.2.##..H.........##...W",
  "W...##............##...W",
  "W...##............##...W",
  "W...##............##...W",
  "W.c.##............##...W",
  "W...##.B........B.##...W",
  "W...##............##...W",
  "W...################...W",
  "W...######====######...W",
  "W......................W",
  "W...........c..........W",
  "W.....3..........3.....W",
  "W.S........P.........S.W",
  "W......................W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const scrapyard = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W~~~~~V~~~~~~~~~~~~~~~~W",
  "W~~~~~~~~~~~V~~~~~~~~~~W",
  "W~~..................~~W",
  "W~~.................X~~W",
  "W~~...........2......~~W",
  "W~~........E.........~~W",
  "W~~..................~~W",
  "W~~..............vv..~~W",
  "W~V..E...........vv..~~W",
  "W~~..................~~W",
  "W~~................D.~~W",
  "W~~......==..........~~W",
  "W~~......==..........V~W",
  "W~~...vv.............~~W",
  "W~~...vv........H....~~W",
  "W~~..........=.......~~W",
  "W~~S.........=.......~~W",
  "W~~..................~~W",
  "W~~~~~~~~~~~~~~V~~~~~~~W",
  "W~~~~~~~V~~~~~~~~~~~~~~W",
  "W~~..................~~W",
  "W~~..................~~W",
  "W~~....1.............~~W",
  "W~~..................~~W",
  "W~~.....B...B.....B..~VW",
  "W~~............cc....~~W",
  "W~~............cc....~~W",
  "W~~..................~~W",
  "WV~.....B...B.....B..~~W",
  "W~~..cc..............~~W",
  "W~~..cc..............~~W",
  "W~~......T...........~~W",
  "W~~.....B...B.....B..~~W",
  "W~~..............+...~~W",
  "W~~..................~~W",
  "W~~........P........S~~W",
  "W~~~~~~~~~~~~~~~~~~~~~~W",
  "W~~~~~~~~~~~~~~~~~~~~~~W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const maze = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W.............=........W",
  "W.............=........W",
  "W.###.........=.......SW",
  "W.#X#......3..=...=....W",
  "W.###...=.....=...=.T..W",
  "W....=..=.....=...=....W",
  "W....=..=======........W",
  "W....=..=..........====W",
  "W....=.B..........=....W",
  "W....=......=...2.=....W",
  "W....=......=.....=....W",
  "W=====......=.....=....W",
  "W.......=...=======....W",
  "W.......=..............W",
  "W.......=.E.........E..W",
  "W.T.....=......B.......W",
  "W.......=..............W",
  "W.......=..............W",
  "W==============...T....W",
  "W...............=...=..W",
  "W...............=...=..W",
  "W...-...........=...=..W",
  "W...-..B......-.=...=..W",
  "W...........E+-.=====..W",
  "W......................W",
  "W=========.............W",
  "W........=.............W",
  "W........=.............W",
  "W........=.===.........W",
  "W.T.=....=.=..=........W",
  "W...=....=.=..=.=...=..W",
  "W...=..1.=.=..=.=.H.=..W",
  "W====....=....=.=...=..W",
  "W...=....=....=.=...=..W",
  "W...=.====....=.=====..W",
  "W...=.........=........W",
  "W..........P..........SW",
  "W......................W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const quarry = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W...................~~.W",
  "W.................##~~SW",
  "W.................##~~.W",
  "W......T..2.......##~~.W",
  "W..E...........G....~~.W",
  "W...................~~.W",
  "W...................~~.W",
  "W...................~~.W",
  "W.....###.........##~~XW",
  "W.....###.........##~~.W",
  "W.....###.........##~~.W",
  "W...................~V.W",
  "W.###......###......~~.W",
  "W.###......###.....T~~.W",
  "W.###......###......~~.W",
  "W...................~~.W",
  "W......###........##~~.W",
  "W......###........##~~.W",
  "W......###........##~~.W",
  "W..B......B.BBBBB...~~.W",
  "W.....B.B...B###B...~~.W",
  "W...........B#3#B...~~.W",
  "W......###..B###B...~~.W",
  "W......###..BBBBB...~~.W",
  "W.###..###........##~~.W",
  "W.###.............##~~.W",
  "W.###.............##~~.W",
  "W...................~~.W",
  "W...................~~.W",
  "W..........###.....M~~.W",
  "W..........###......~~.W",
  "W.....###..###......~~.W",
  "W.....###.........##~~.W",
  "W.....###.......E.##~V.W",
  "W.........1.......##~~.W",
  "W...................~~.W",
  "W..........P........~~SW",
  "W...................~~.W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const barracks = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W....................X.W",
  "WS....................SW",
  "W..........=...........W",
  "W..........=...........W",
  "W..........=...........W",
  "W..........=...........W",
  "W..##=##...=...#####...W",
  "W..#...#.......#...#...W",
  "W..#...#.......#.E.#...W",
  "W..#.G.#.......#...#...W",
  "W..#...#.....B.#...#...W",
  "W..#####.2.T...##=##...W",
  "W......................W",
  "W......................W",
  "W..##=##.......#####.D.W",
  "W..#...#.......#...#...W",
  "W..#...#...=.B.#.E.#...W",
  "W..#.E.#...=...#...#...W",
  "W..#...#...=...#...#...W",
  "W..#####...=...##=##.M.W",
  "W..........=+..........W",
  "W......................W",
  "W..##=##.....B.#####...W",
  "W..#...#.1.....#...#...W",
  "W..#...#.......#.E.#...W",
  "W..#.H.#...T...#...#...W",
  "W..#...#.......#...#...W",
  "W..#####.......##=##...W",
  "W............B.........W",
  "W......................W",
  "W..##=##...=...#####...W",
  "W..#...#...=...#...#...W",
  "W..#...#...=...#.E.#.3.W",
  "W..#.E.#...=...#...#...W",
  "W..#...#...=...#...#...W",
  "W..#####.......##=##...W",
  "WS.........P..........SW",
  "W......................W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

const twins = [
  "WWWWWWWWWWWWWWWWWWWWWWWW",
  "W......................W",
  "W......................W",
  "W..##....BB.@.BB.##....W",
  "WS.##............##...SW",
  "W..##..Z.........##..M.W",
  "W......................W",
  "W......................W",
  "W.....................DW",
  "W......................W",
  "W......................W",
  "W........cc...cc.......W",
  "W..=====.cc...cc.=====.W",
  "W..=.2.=.........=.3.=.W",
  "W..=...=.........=...=.W",
  "W..=====.........=====.W",
  "W......................W",
  "W......................W",
  "W.........####.........W",
  "W.........#XX..........W",
  "W.........#XX..........W",
  "W.........####.........W",
  "W..=====.........=====.W",
  "W..=...=.........=...=.W",
  "W..=.1.=.........=.3.=.W",
  "W..=====.........=====.W",
  "W......................W",
  "W........cc...cc.......W",
  "W........cc...cc.......W",
  "W......................W",
  "W......................W",
  "W.....................DW",
  "W..##..Z.........##....W",
  "W..##............##..M.W",
  "W..##............##....W",
  "W......................W",
  "W........BB...BB.......W",
  "WS..........P.........SW",
  "W......................W",
  "WWWWWWWWWWWWWWWWWWWWWWWW",
];

export const levels: Level[] = [
  { name: "Yard", objective: "Blast a hole in the wall and reach the exit", par: 45, map: yard },
  { name: "Alley", objective: "Turrets hold the alley. Take the cover with you", par: 70, map: alley },
  { name: "Scrapyard", objective: "Mind the traffic. Everything here is loose", par: 75, map: scrapyard },
  { name: "Depot", objective: "Barrels go off in chains. Use them", par: 80, map: depot },
  { name: "Maze", objective: "Brick walls are a suggestion. Cut your own way", par: 110, map: maze },
  { name: "Fortress", objective: "The exit is inside. Break the gate", par: 100, map: fort },
  { name: "Quarry", objective: "A ring of barrels round the prize. Stand well back", par: 95, map: quarry },
  { name: "Barracks", objective: "Eight in their bunks. Wake them one at a time", par: 120, map: barracks },
  { name: "Twins", objective: "Two of them. The exit is walled until both are gone", par: 140, map: twins },
  { name: "HQ", objective: "One target. It has more guns than you", par: 120, map: hq },
];

/** Every character the parser knows. Anything else is a mistake in a map. */
export const LEGEND = ".W#=-BcvPTEZSX123+@HGMD~VF";

const isEnemy = (ch: string) => "TEZHGMD".includes(ch);
const isProp = (ch: string) => ch === "B" || ch === "c" || ch === "v" || ch === "V";
const isTile = (ch: string) => ch === "#" || ch === "=" || ch === "-";

/** The centre of tile (col, row) in metres. */
export const tileCentre = (col: number, row: number) => ({
  x: (col + 0.5) * TILE,
  y: (row + 0.5) * TILE,
});

/**
 * Text to things. Runs of `W` on one row become a single fixture, because a
 * border of forty separate boxes is forty broadphase entries and one long one
 * is one; everything else stays a tile of its own, since each has its own
 * health and has to come apart on its own.
 */
export function parseLevel(map: string[]): ParsedLevel {
  const rows = map.length;
  const cols = map[0]?.length ?? 0;
  const out: ParsedLevel = {
    cols,
    rows,
    walls: [],
    tiles: [],
    props: [],
    enemies: [],
    spawners: [],
    factories: [],
    pickups: [],
    exits: [],
    roads: [],
    player: tileCentre(1, 1),
  };

  // A run of wall that sits exactly under the same run in the row above
  // grows that body downwards instead of starting its own, so a tall map's
  // side walls are one body each, not one per row.
  const open = new Map<string, { wall: ParsedLevel["walls"][number]; top: number; last: number }>();

  for (let row = 0; row < rows; row++) {
    const line = map[row] ?? "";
    let run = 0;
    for (let col = 0; col <= cols; col++) {
      const ch = line[col] ?? "";
      if (ch === "W") {
        run += 1;
        continue;
      }
      if (run > 0) {
        const start = col - run;
        const key = `${start},${run}`;
        const above = open.get(key);
        if (above && above.last === row - 1) {
          above.last = row;
          above.wall.y = ((above.top + row + 1) / 2) * TILE;
          above.wall.hh = ((row - above.top + 1) * TILE) / 2;
        } else {
          const wall = { x: (start + run / 2) * TILE, y: (row + 0.5) * TILE, hw: (run * TILE) / 2, hh: TILE / 2 };
          out.walls.push(wall);
          open.set(key, { wall, top: row, last: row });
        }
        run = 0;
      }
      if (!ch || ch === ".") continue;
      const cell: Cell = { ...tileCentre(col, row), ch };
      if (isTile(ch)) out.tiles.push(cell);
      else if (isProp(ch)) out.props.push(cell);
      else if (isEnemy(ch)) out.enemies.push(cell);
      else if (ch === "S") out.spawners.push(cell);
      else if (ch === "F") out.factories.push(cell);
      else if (ch === "X") out.exits.push(cell);
      else if ("123+@".includes(ch)) out.pickups.push(cell);
      else if (ch === "P") out.player = tileCentre(col, row);
      else if (ch === "~") out.roads.push(cell);
      // A car stands on the street, so its tile is road as well as a car,
      // otherwise it leaves a hole in the road it is supposed to drive.
      if (ch === "V") out.roads.push({ ...cell, ch: "~" });
    }
  }

  return out;
}

// ---- the streets ------------------------------------------------------------

/** The four ways a car may face, as steps on the grid. */
export const DIRS: ReadonlyArray<readonly [number, number]> = [[1, 0], [0, 1], [-1, 0], [0, -1]];

export type RoadStep = { lane: number; col: number; row: number };

/**
 * Where a car goes next, keeping right.
 *
 * From the tile it is in: if there is more road to its right it moves over,
 * so on a two-lane street the two directions pass each other rather than
 * meet. Then it looks one tile ahead down that lane. Only when the lane runs
 * out does it turn: right if it can, then left, then back the way it came.
 * On a road that closes into a loop that is enough to go round for ever.
 *
 * `choose` picks between turns where more than one is open; it is a
 * parameter so a test can be told which way to go.
 */
export function roadStep(
  isRoad: (col: number, row: number) => boolean,
  col: number,
  row: number,
  lane: number,
  choose: (open: number[]) => number = (open) => open[0] as number,
): RoadStep {
  const step = (l: number) => DIRS[((l % 4) + 4) % 4] as readonly [number, number];
  const keepRight = (l: number, c: number, r: number): [number, number] => {
    const [dx, dy] = step(l);
    // The tile to the right is (-dy, dx) away.
    return isRoad(c - dy, r + dx) ? [c - dy, r + dx] : [c, r];
  };

  let [lc, lr] = keepRight(lane, col, row);
  let [dx, dy] = step(lane);
  if (isRoad(lc + dx, lr + dy)) return { lane, col: lc + dx, row: lr + dy };

  // The lane runs out. Right, then left, then back.
  const open = [(lane + 1) % 4, (lane + 3) % 4, (lane + 2) % 4].filter((l) => {
    const [tx, ty] = step(l);
    return isRoad(col + tx, row + ty);
  });
  if (!open.length) return { lane, col, row };
  const next = open.length > 1 ? choose(open) : (open[0] as number);
  [lc, lr] = keepRight(next, col, row);
  [dx, dy] = step(next);
  const ahead: [number, number] = isRoad(lc + dx, lr + dy) ? [lc + dx, lr + dy] : [col + dx, row + dy];
  return { lane: next, col: ahead[0], row: ahead[1] };
}
